import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './Docs.css';

export default function WhosOnlineApi() {
  return (
    <div className="docs-lite">
      <Helmet>
        <link rel="canonical" href="https://www.joegalaxy.net/docs/whos-online-api" />
      </Helmet>

      <header className="docs-lite__header">
        <h1>Designing a Who’s Online API (Users vs Sessions)</h1>
      </header>

      <main className="docs-lite__content">
        <p>
          “Who’s online?” looks simple.
          <br />
          In real systems, it is one of the most commonly mis-modeled APIs.
        </p>
        <p>
          This guide explains how to design a reliable who’s online API by separating{' '}
          <strong>sessions from users</strong>, deriving presence from signals, and avoiding
          misleading counts.
        </p>

        <hr />

        <section>
          <h2>Why naïve “online users” APIs fail</h2>
          <p>Most implementations start with a user-level flag:</p>
          <pre>
            <code>{`user.isOnline = true;`}</code>
          </pre>
          <p>This approach fails immediately.</p>
          <p>A single user can:</p>
          <ul>
            <li>open multiple tabs</li>
            <li>use multiple devices</li>
            <li>background a session</li>
            <li>lose network connectivity transiently</li>
          </ul>
          <p>If you toggle a user-level boolean on connect and disconnect, your API will:</p>
          <ul>
            <li>undercount active users</li>
            <li>overcount availability</li>
            <li>flap between states</li>
            <li>mislead downstream systems</li>
          </ul>
          <p>
            <strong>Online is not a property of a user.</strong>
            <br />
            It is an <strong>emergent property of active sessions over time</strong>.
          </p>
        </section>

        <hr />

        <section>
          <h2>Sessions vs users (the core distinction)</h2>
          <p>
            A <strong>session</strong> represents a single runtime instance:
          </p>
          <ul>
            <li>a browser tab</li>
            <li>a mobile app instance</li>
            <li>an SDK client</li>
          </ul>
          <p>
            A <strong>user</strong> may have many sessions.
          </p>
          <p>Correct presence modeling always starts with sessions:</p>
          <pre>
            <code>{`user
 ├─ session A (active)
 ├─ session B (idle)
 └─ session C (stale)`}</code>
          </pre>
          <p>User-level presence must be <strong>derived</strong>, not stored.</p>
        </section>

        <hr />

        <section>
          <h2>What a who’s online API should return</h2>
          <p>Instead of returning a flat list of users, a reliable API exposes:</p>
          <ul>
            <li>active sessions</li>
            <li>derived user presence</li>
            <li>timestamps</li>
            <li>confidence indicators</li>
          </ul>
          <p>Example conceptual response:</p>
          <pre>
            <code>{`{
  "activeSessions": 12,
  "users": [
    {
      "userId": "u123",
      "derivedState": "active",
      "lastSeenAt": "2025-02-10T12:01:30Z",
      "sessionCount": 2
    }
  ]
}`}</code>
          </pre>
          <p>The key is transparency:</p>
          <ul>
            <li>expose <strong>when</strong> presence was last observed</li>
            <li>do not hide uncertainty behind booleans</li>
          </ul>
        </section>

        <hr />

        <section>
          <h2>Concurrency vs totals</h2>
          <p>A common mistake is confusing:</p>
          <ul>
            <li>total users</li>
            <li>currently active users</li>
            <li>concurrent sessions</li>
          </ul>
          <p>Concurrency is what matters for:</p>
          <ul>
            <li>capacity planning</li>
            <li>coordination</li>
            <li>real-time UX</li>
            <li>A/V readiness</li>
          </ul>
          <p>A who’s online API should make concurrency explicit.</p>
        </section>

        <hr />

        <section>
          <h2>Deriving user presence safely</h2>
          <p>User-level presence is derived from session states using precedence rules:</p>
          <ul>
            <li>
              <strong>active</strong> beats idle
            </li>
            <li>
              <strong>idle</strong> beats away
            </li>
            <li>
              <strong>away</strong> beats stale
            </li>
            <li>
              <strong>stale</strong> beats offline
            </li>
          </ul>
          <p>This prevents:</p>
          <ul>
            <li>marking users offline when one tab closes</li>
            <li>flapping caused by transient disconnects</li>
          </ul>
          <p>Presence is computed at read time, not written as state.</p>
        </section>

        <hr />

        <section>
          <h2>Avoiding misleading counts</h2>
          <p>
            Do <strong>not</strong>:
          </p>
          <ul>
            <li>store <code>onlineUserCount</code> in a database</li>
            <li>increment/decrement counters on connect/disconnect</li>
            <li>broadcast counts without timestamps</li>
          </ul>
          <p>Counts without context are lies.</p>
          <p>Instead:</p>
          <ul>
            <li>derive counts from live session state</li>
            <li>expose time windows</li>
            <li>allow consumers to choose thresholds</li>
          </ul>
        </section>

        <hr />

        <section>
          <h2>Who consumes a who’s online API</h2>
          <p>A reliable who’s online API enables:</p>
          <ul>
            <li>activity indicators</li>
            <li>readiness gates</li>
            <li>call initiation logic</li>
            <li>live dashboards</li>
            <li>coordination workflows</li>
          </ul>
          <p>
            It is <strong>not</strong> just a UI convenience.
            <br />
            It is a coordination primitive.
          </p>
        </section>

        <hr />

        <section>
          <h2>Summary</h2>
          <p>A correct who’s online API:</p>
          <ul>
            <li>
              models <strong>sessions</strong>, not users
            </li>
            <li>
              derives presence from <strong>signals over time</strong>
            </li>
            <li>exposes timestamps and uncertainty</li>
            <li>distinguishes concurrency from totals</li>
          </ul>
          <p>When designed this way, presence becomes trustworthy instead of brittle.</p>
        </section>

        <hr />

        <section>
          <h2>Related docs</h2>
          <ul>
            <li>
              <Link to="/docs/index.md">Docs index</Link>
            </li>
            <li>
              <Link to="/docs/presence-online-offline.md">Online vs Offline Presence</Link>
            </li>
            <li>
              <Link to="/docs/whos-online-api.md">Who’s Online API</Link>
            </li>
            <li>
              <Link to="/docs/presence-socket-io.md">Presence with Socket.IO</Link>
            </li>
            <li>
              <Link to="/docs/webrtc-presence.md">WebRTC Presence</Link>
            </li>
          </ul>
        </section>

        <p className="docs-lite__source">
          —
          <br />
          See this model running live:
          <br />
          <a href="https://www.joegalaxy.net/presence-demo">
            https://www.joegalaxy.net/presence-demo
          </a>
          <br />
          <a href="https://github.com/thejoegalaxy/presence-engine/blob/main/docs/whos-online-api.md">
            View source on GitHub
          </a>
        </p>
      </main>
    </div>
  );
}
